import config from "@/config";
import { CartHook } from "@/lib/hooks/useCart";
import { FormattedSelection } from "../formatting/formatSelection";

const getRecipesCount = (
  cart: CartHook["cart"] | FormattedSelection["selection"]
): {
  recipesCount: number;
  requiredYarns: number;
} => {
  const items = cart.items.flatMap((item) => item.items);
  const recipes = items?.filter((item) => item?.type === "recipe");

  const recipesCount = recipes
    .map((recipe) => recipe.quantity)
    .reduce((a, b) => a + b, 0);

  const requiredYarns = recipes
    .map(
      (recipe) =>
        +(recipe.requiredYarns ?? config.YarnToRecipeRatio) * recipe.quantity
    )
    .reduce((a, b) => a + b, 0);

  return { recipesCount, requiredYarns };
};

const getYarnsCount = (
  cart: CartHook["cart"] | FormattedSelection["selection"]
): number => {
  const items = cart.items.flatMap((item) => item.items).filter(Boolean);
  const yarns = items?.filter((item) => item.type === "yarn");

  const count = yarns.map((yarn) => yarn.quantity).reduce((a, b) => a + b, 0);

  return count;
};

const getBookletsCount = (
  cart: CartHook["cart"] | FormattedSelection["selection"]
): {
  bookletCount: number;
  requiredYarns: number;
} => {
  const items = cart.items.flatMap((item) => item.items).filter(Boolean);
  const booklets = items?.filter((item) => item.type === "booklet");

  const bookletCount = booklets
    .map((booklet) => booklet.quantity)
    .reduce((a, b) => a + b, 0);

  const requiredYarns = booklets
    .map(
      (booklet) =>
        +(booklet.requiredYarns ?? config.YarnToRecipeRatio) * booklet.quantity
    )
    .reduce((a, b) => a + b, 0);

  return { bookletCount, requiredYarns };
};

const recipesText = (count: number) => {
  if (count === 0) return "";
  const text = count === 1 ? "oppskrift" : "oppskrifter";
  return text;
};

const yarnsText = (count: number) => {
  if (count === 0) return "";
  const text = count === 1 ? "nøste" : "nøster";
  return text;
};

const bookletsText = (count: number) => {
  if (count === 0) return "";
  const text = count === 1 ? "hefte" : "hefter";
  return text;
};

export const missingText = (
  recipesCount: number,
  bookletCount: number,
  missingCount: number
) => {
  const recipes = recipesText(recipesCount);
  const booklets = bookletsText(bookletCount);
  const yarns = yarnsText(missingCount);

  // Du må kjøpe [amount] garn til for å kunne kjøpe [amount] oppskrifter og [amount] hefter.

  if (recipesCount === 0) {
    return `Du må kjøpe ${missingCount} garnnøster for å kunne kjøpe ${bookletCount} ${booklets}.`;
  }

  if (bookletCount === 0) {
    return `Du må kjøpe ${missingCount} garnnøster for å kunne kjøpe ${recipesCount} ${recipes}.`;
  }

  const text = `Du må kjøpe ${missingCount} ${yarns} for å kunne kjøpe ${recipesCount} ${recipes} og ${bookletCount} ${booklets}.`;

  return text;
};

const missingYarnsCount = (
  cart: CartHook["cart"] | FormattedSelection["selection"]
) => {
  let count = 0;

  const { recipesCount, requiredYarns: recipeYarnsRequired } =
    getRecipesCount(cart);
  const yarnsCount = getYarnsCount(cart);
  const { bookletCount, requiredYarns: bookletYarnsRequired } =
    getBookletsCount(cart);

  const requiredYarns = recipeYarnsRequired + bookletYarnsRequired;

  count = requiredYarns - yarnsCount;

  const text = missingText(recipesCount, bookletCount, count);

  return {
    missingCount: count,
    text,
    yarnsCount,
    recipesCount,
    bookletCount,
    isMissing: count > 0,
  };
};

export type MissingYarnsCount = ReturnType<typeof missingYarnsCount>;
export default missingYarnsCount;
